import { useMemo, type ReactNode } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight, Minus, type LucideIcon } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';
import type { PermissionKey } from '@supernova/shared';
import { useReportsEnabled } from '@/hooks/use-reports-access';
import { usePermissions } from '@/hooks/use-permissions';
import { StatusBadge } from '@/components/shared/status-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { formatTrendBucket, type Delta } from './use-home-analytics';

/**
 * Shared shell so every card on the page sits on the same surface.
 *
 * `reportId` turns the heading row into a drill-down: it deep-links to the
 * matching report via `#/reports?tab=pos&report=<id>&from=…&to=…`, which the
 * reports page reads to open that report on the same period the card is
 * showing. Without the range the link would land on the report's own default
 * of today — the same report, different numbers. Cards whose data has no
 * single backing report simply omit it.
 *
 * `hint` is a line of help text under the title saying where View Details
 * goes. It shows only alongside the link, so it never describes a link the
 * viewer can't see.
 */
export function Panel({
  title,
  reportId,
  href,
  requires,
  range,
  hint,
  children,
  className,
}: {
  title: string;
  reportId?: string;
  /** Plain drill-down target for cards that aren't backed by a POS report —
   *  e.g. a module home linking into its module page. Ignored when `reportId`
   *  is set. */
  href?: string;
  /** The key that opens `href`'s screen; the link is left out without it. */
  requires?: PermissionKey;
  /** Carried into the report so it opens on the period shown here. */
  range?: { dateFrom: string; dateTo: string };
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  const reportsEnabled = useReportsEnabled();
  const canSeeReport = usePermissions().has('reports.pos.view');
  const showHint = !!hint && !!reportId && reportsEnabled && canSeeReport;
  return (
    <section className={cn('rounded-xl border border-border bg-white p-4', className)}>
      <div className="mb-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {reportId ? (
            <ViewDetailsLink reportId={reportId} range={range} />
          ) : href ? (
            <ViewDetailsLink href={href} requires={requires} />
          ) : null}
        </div>
        {showHint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/**
 * The drill-down link every home card shares: opens the matching report on
 * the period the card is showing (see {@link Panel} for the URL contract).
 */
export function ViewDetailsLink({
  reportId,
  href,
  requires,
  range,
}: {
  reportId?: string;
  /** Used instead of a report link; not subject to the Reports module gate. */
  href?: string;
  /** The key that opens `href`'s screen. */
  requires?: PermissionKey;
  range?: { dateFrom: string; dateTo: string };
}) {
  // No drill-down while the super admin has Reports switched off, or to a
  // screen the user can't open — the link would only bounce back to Home.
  const reportsEnabled = useReportsEnabled();
  const permissions = usePermissions();
  if (href) {
    if (requires && !permissions.has(requires)) return null;
  } else if (!reportId || !reportsEnabled || !permissions.has('reports.pos.view')) {
    return null;
  }
  const query = range ? `&from=${encodeURIComponent(range.dateFrom)}&to=${encodeURIComponent(range.dateTo)}` : '';
  return (
    <a
      href={href ?? `#/reports?tab=pos&report=${reportId}${query}`}
      className="gradient-border-animated inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Gradient text: the wash is painted as a background and clipped to
          the glyphs. The icon keeps a solid fill matching the gradient's
          end, since `currentColor` would inherit the transparent text. */}
      <span className="bg-linear-to-r from-orange-500 via-pink-500 to-blue-500 bg-clip-text text-transparent">
        View Details
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-blue-500" />
    </a>
  );
}

export function CardSkeleton({ height = 'h-24' }: { height?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-muted', height)} />;
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;
}

/** What an empty card says instead of its data. */
export type EmptyCopy = { icon: LucideIcon; title: string; description: string };

/**
 * Empty state for a home card: an icon, a headline, and one line on what will
 * appear here. `height` holds the card at roughly its loaded size (the loading
 * skeleton's height), so an empty period doesn't collapse the grid.
 *
 * `ghost` optionally paints a faded, figure-free sample of the card's visual
 * behind the message — used only where the card is tall enough that the
 * message sits clear of it (the trend chart).
 */
export function CardEmpty({
  empty,
  height = 'min-h-40',
  ghost,
}: {
  empty: EmptyCopy;
  height?: string;
  ghost?: ReactNode;
}) {
  return (
    <div className={cn('relative flex flex-col justify-center', height)}>
      {ghost && (
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none opacity-60">
          {ghost}
        </div>
      )}
      <EmptyState icon={empty.icon} title={empty.title} description={empty.description} className="py-0" />
    </div>
  );
}

/** Faded sample area chart behind {@link SalesTrend}'s empty state. */
function GhostTrend() {
  return (
    <svg viewBox="0 0 300 120" preserveAspectRatio="none" className="h-full w-full">
      {[24, 54, 84, 114].map((y) => (
        <line
          key={y}
          x1="0"
          x2="300"
          y1={y}
          y2={y}
          stroke="var(--color-border)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      <path
        d="M0 100 C30 98 50 80 80 82 S130 58 160 62 S220 36 250 44 S285 30 300 28 L300 114 L0 114Z"
        fill="var(--color-muted)"
      />
      <path
        d="M0 100 C30 98 50 80 80 82 S130 58 160 62 S220 36 250 44 S285 30 300 28"
        fill="none"
        stroke="var(--color-border)"
        strokeWidth="2"
        strokeDasharray="5 5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Change against the preceding period. A null pct means there was no baseline
 * to divide by, which is stated rather than shown as 0% — a flat arrow next to
 * "0%" would claim the business held steady when in fact nothing came before.
 */
export function DeltaBadge({ delta, invert = false }: { delta: Delta; invert?: boolean }) {
  if (delta.pct === null) {
    return <span className="text-xs text-muted-foreground">No prior period</span>;
  }
  const rising = delta.pct > 0;
  const flat = Math.abs(delta.pct) < 0.0005;
  // Refunds rising is bad news, so the colour follows meaning rather than sign.
  const good = invert ? !rising : rising;
  const Icon = flat ? Minus : rising ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
        flat ? 'text-muted-foreground' : good ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(delta.pct * 100).toFixed(1)}%
    </span>
  );
}

/** A headline figure, its change vs last period, and a sparkline of the range. */
export function KpiCard({
  label,
  value,
  delta,
  previous,
  series,
  dataKey,
  invert = false,
  footnote,
  reportId,
  range,
}: {
  label: string;
  value: string;
  /** Change vs the previous period; omitted when there is nothing to compare to. */
  delta?: Delta;
  previous?: string;
  series: Array<Record<string, number | string>>;
  dataKey: string;
  invert?: boolean;
  footnote?: string;
  /** Optional drill-down, same contract as {@link Panel}: the report to open
   *  and the period to open it on. */
  reportId?: string;
  range?: { dateFrom: string; dateTo: string };
}) {
  const stroke = invert ? 'var(--color-muted-foreground)' : 'var(--color-primary)';
  // An all-zero range would draw a solid line along the floor, which reads as a
  // real (flat) trend. A faint dashed baseline says "nothing yet" instead.
  const flat = series.every((pt) => !Number(pt[dataKey]));
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-border bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {reportId && <ViewDetailsLink reportId={reportId} range={range} />}
      </div>
      <span className="text-2xl font-bold tabular-nums leading-tight text-foreground">{value}</span>
      {(delta || footnote) && (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {delta && <DeltaBadge delta={delta} invert={invert} />}
          <span className="text-xs text-muted-foreground">{footnote ?? `vs ${previous}`}</span>
        </div>
      )}
      <div className="h-10">
        {series.length > 1 && flat && (
          <div aria-hidden="true" className="flex h-full items-end">
            <div className="w-full border-t border-dashed border-border" />
          </div>
        )}
        {series.length > 1 && !flat && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={stroke}
                strokeWidth={1.75}
                fill={stroke}
                fillOpacity={0.1}
                isAnimationActive={false}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

/** Horizontal magnitude bars — used for top items, departments and categories. */
export function BarList({
  rows,
  empty,
}: {
  rows: Array<{ id: string; name: string; value: string; share: number }>;
  empty: EmptyCopy;
}) {
  if (rows.length === 0) return <CardEmpty empty={empty} />;
  const max = Math.max(...rows.map((r) => r.share), 0.0001);
  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-sm text-foreground" title={r.name}>
              {r.name}
            </span>
            <span className="shrink-0 text-sm tabular-nums text-muted-foreground">{r.value}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.max(2, (r.share / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

const METHOD_COLORS = ['#00838d', '#16a34a', '#d97706', '#2563eb', '#7c3aed', '#64748b'];

/** Payment split as one stacked bar plus a legend carrying the real figures. */
export function PaymentSplit({
  rows,
  total,
  empty,
}: {
  rows: Array<{ method: string; total: number; percent: number }>;
  total: number;
  empty: EmptyCopy;
}) {
  if (rows.length === 0 || total <= 0) return <CardEmpty empty={empty} />;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
        {rows.map((r, i) => (
          <div
            key={r.method}
            style={{ width: `${Math.max(0.5, r.percent)}%`, background: METHOD_COLORS[i % METHOD_COLORS.length] }}
            title={`${r.method}: ${formatCurrency(r.total)}`}
          />
        ))}
      </div>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <li key={r.method} className="flex items-center gap-2 text-sm">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ background: METHOD_COLORS[i % METHOD_COLORS.length] }}
            />
            <span className="min-w-0 flex-1 truncate capitalize text-foreground">{r.method}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {formatCurrency(r.total)} ({r.percent.toFixed(1)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Theme tokens rather than literals, so the card follows the palette (and any
// tenant re-theme) the way the charts above it already do. Same teal pair as
// the Sales Trends lines: solid primary, and the previous-period line's
// primary at 45% opacity.
const CUSTOMER_MIX_COLORS = {
  newCustomers: 'var(--color-primary)',
  returningCustomers: 'color-mix(in srgb, var(--color-primary) 45%, transparent)',
};

/**
 * New vs returning customers as one stacked bar plus a legend carrying the
 * counts. Same shape as {@link PaymentSplit}, so the two cards read alike.
 *
 * `anonymousSales` is shown as a footnote rather than a third slice: walk-in
 * sales aren't a category of customer, they're the sales this split can say
 * nothing about, and folding them in would overstate how many people the
 * percentages describe.
 */
export function CustomerMix({
  newCustomers,
  returningCustomers,
  anonymousSales,
  empty,
}: {
  newCustomers: number;
  returningCustomers: number;
  anonymousSales: number;
  empty: EmptyCopy;
}) {
  const total = newCustomers + returningCustomers;
  if (total <= 0) {
    return (
      <div className="flex flex-col gap-3">
        <CardEmpty empty={empty} />
        {/* Sales still happened, just none with a customer attached — say so,
            or the card reads as "no business" rather than "no customer data". */}
        {anonymousSales > 0 && (
          <p className="text-xs text-muted-foreground">
            {formatNumber(anonymousSales)} sale{anonymousSales === 1 ? '' : 's'} with no customer attached.
          </p>
        )}
      </div>
    );
  }
  const rows = [
    { key: 'newCustomers' as const, label: 'New Customers', value: newCustomers },
    { key: 'returningCustomers' as const, label: 'Returning Customers', value: returningCustomers },
  ];
  // A zero-width slice would still paint its 0.5px gap, so only non-empty
  // halves go into the bar. Both always stay in the legend.
  const slices = rows.filter((r) => r.value > 0);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
        {slices.map((r) => (
          <div
            key={r.key}
            style={{ width: `${(r.value / total) * 100}%`, background: CUSTOMER_MIX_COLORS[r.key] }}
            title={`${r.label}: ${formatNumber(r.value)}`}
          />
        ))}
      </div>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: CUSTOMER_MIX_COLORS[r.key] }} />
            <span className="min-w-0 flex-1 truncate text-foreground">{r.label}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {formatNumber(r.value)} ({((r.value / total) * 100).toFixed(2)}%)
            </span>
          </li>
        ))}
      </ul>
      {anonymousSales > 0 && (
        <p className="text-xs text-muted-foreground">
          {formatNumber(anonymousSales)} sale{anonymousSales === 1 ? '' : 's'} with no customer attached, not counted
          above.
        </p>
      )}
    </div>
  );
}

/**
 * Sales and transaction counts across the range, on their own axes.
 *
 * `previous` lays the preceding period over the same axis as a dotted line,
 * aligned by position (bucket 1 against bucket 1). It may be longer than
 * `series` — today's hours so far against all of yesterday — in which case
 * the axis runs on to the end of it, so the line shows what the rest of the
 * day looked like last time. `dataKey` picks the money series to plot (the
 * home card plots `sales`, the Sales Trends report plots `net`).
 */
export function SalesTrend({
  series,
  previous,
  dataKey = 'sales',
  label = 'Sales',
  empty,
}: {
  series: Array<Record<string, number | string> & { date: string; transactions: number }>;
  previous?: Array<Record<string, number | string> & { date: string }>;
  dataKey?: string;
  label?: string;
  /** Opt-in icon-and-text empty state. Hourly buckets are zero-filled, so without it an
   *  empty day plots a flat line along the floor. Callers that omit it keep the
   *  plain note and the flat line. */
  empty?: EmptyCopy;
}) {
  const data = useMemo(() => {
    const n = Math.max(series.length, previous?.length ?? 0);
    const rows: Array<{ date: string; value: number | null; transactions: number | null; previous: number | null }> =
      [];
    for (let i = 0; i < n; i++) {
      const cur = series[i];
      const prev = previous?.[i];
      rows.push({
        date: (cur?.date ?? prev?.date) as string,
        value: cur ? (cur[dataKey] as number) : null,
        transactions: cur ? cur.transactions : null,
        previous: prev ? (prev[dataKey] as number) : null,
      });
    }
    return rows;
  }, [series, previous, dataKey]);
  const allZero = series.every((pt) => !Number(pt[dataKey]) && !pt.transactions);
  if (empty && (data.length < 2 || allZero) && !previous?.some((pt) => Number(pt[dataKey]))) {
    return <CardEmpty empty={empty} height="h-56" ghost={<GhostTrend />} />;
  }
  if (data.length < 2) return <EmptyNote>Not enough data in this period to plot a trend</EmptyNote>;
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => formatTrendBucket(value)}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            yAxisId="sales"
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={(v: number) => formatCurrency(v).replace(/\.00$/, '')}
          />
          <YAxis yAxisId="txn" orientation="right" hide />
          <Tooltip
            labelFormatter={(l: string) => formatTrendBucket(l, true)}
            formatter={(value: number, name: string) =>
              name === 'value'
                ? [formatCurrency(value), label]
                : name === 'previous'
                  ? [formatCurrency(value), 'Previous period']
                  : [formatNumber(value), 'Transactions']
            }
            contentStyle={{
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              fontSize: 12,
            }}
          />
          {previous && (
            <Line
              yAxisId="sales"
              type="monotone"
              dataKey="previous"
              stroke="var(--color-primary)"
              strokeOpacity={0.45}
              strokeWidth={1.5}
              strokeDasharray="2 3"
              dot={false}
              isAnimationActive={false}
            />
          )}
          <Area
            yAxisId="sales"
            type="monotone"
            dataKey="value"
            stroke="var(--color-primary)"
            strokeWidth={2}
            fill="var(--color-primary)"
            fillOpacity={0.1}
            isAnimationActive={false}
          />
          <Line
            yAxisId="txn"
            type="monotone"
            dataKey="transactions"
            stroke="var(--color-muted-foreground)"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Subtotal → net walk, so the headline figure can be reconciled at a glance. */
export function SalesBreakdown({
  rows,
}: {
  rows: Array<{ label: string; value: number; emphasis?: boolean; negative?: boolean }>;
}) {
  return (
    <dl className="flex flex-col">
      {rows.map((r) => (
        <div
          key={r.label}
          className={cn(
            'flex items-baseline justify-between border-b border-border/60 py-2 text-sm last:border-b-0',
            r.emphasis && 'font-semibold text-foreground',
          )}
        >
          <dt className={cn(!r.emphasis && 'text-muted-foreground')}>{r.label}</dt>
          <dd className="tabular-nums">
            {r.negative && r.value > 0 ? '−' : ''}
            {formatCurrency(r.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface ActivityRow {
  id: string;
  primary: string;
  secondary?: string;
  value: string;
  /** Rendered as a {@link StatusBadge}; omit for rows without a status. */
  status?: string;
  /** Small text under the value — a date, a frequency. */
  meta?: string;
  /** Tints the meta line red: overdue bills, failed items. */
  alert?: boolean;
}

/** Compact list of recent/upcoming items — used by the module home pages. */
export function ActivityList({ rows, empty }: { rows: ActivityRow[]; empty: EmptyCopy }) {
  if (rows.length === 0) return <CardEmpty empty={empty} />;
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => (
        <li key={r.id} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground" title={r.primary}>
              {r.primary}
            </p>
            {r.secondary && (
              <p className="truncate text-xs text-muted-foreground" title={r.secondary}>
                {r.secondary}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <div className="flex items-center gap-2">
              {r.status && <StatusBadge status={r.status} />}
              <span className="text-sm font-semibold tabular-nums text-foreground">{r.value}</span>
            </div>
            {r.meta && (
              <span className={cn('text-xs', r.alert ? 'text-red-600' : 'text-muted-foreground')}>{r.meta}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
